/**
 * ─── App Providers ───────────────────────────────────────────
 * Wraps the app in all required context providers.
 * Order matters — inner providers can depend on outer ones.
 */

import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ToastProvider } from '@repo/ui-core'
import { ErrorBoundary } from '@repo/ui-core'
import { AuthProvider } from './context/AuthContext'
import { FeatureFlagProvider } from './context/FeatureFlagContext'

// Create a Query Client with sensible defaults for offline-first
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Offline-first: avoid unnecessary refetches
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 2,
      retryDelay: 1000,
    },
  },
})

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <FeatureFlagProvider>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              {children}
            </ToastProvider>
          </QueryClientProvider>
        </FeatureFlagProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}
